import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { firstValueFrom, switchMap, tap } from 'rxjs';
import { ConnectService } from '../connect/connect.service';
import { FeatureFlagsConfigService } from '../feature-flags/feature-flags-config.service';
import { GainsightService } from '../gainsight/gainsight.service';
import { LocaleService } from '../i18n/locale.service';
import { ImportService } from '../import/import.service';
import { IngestionService } from '../ingestion/ingestion.service';
import { injectLogger } from '../logging/logger-injection';
import { FeatureLayerService } from '../map/feature-layer/feature-layer.service';
import { DownloadService } from '../options-panel/download/download.service';
import { ProjectRegionService } from '../project-region/project-region.service';
import { SetIsLoading } from '../scandata/scandata.actions';
import { ScandataService } from '../scandata/scandata.service';
import { IconRegistryService } from '../services/icon-registry.service';
import { UserService } from '../user/user.service';
import { SetContext, SetSettings, SetUrlQuery } from './app.actions';
import { AppContext, AppSettings } from './app.models';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private logger = injectLogger('AppService');

  constructor(
    private store: Store,
    private connectService: ConnectService,
    private localeService: LocaleService,
    private gainsightService: GainsightService,
    private projectRegionService: ProjectRegionService,
    private userService: UserService,
    private scandataService: ScandataService,
    private featureLayerService: FeatureLayerService,
    private featureFlagsConfigService: FeatureFlagsConfigService,
    private iconRegistryService: IconRegistryService,
    private importService: ImportService,
    private downloadService: DownloadService,
    private ingestionService: IngestionService,
  ) {}

  async initialize(window: Window): Promise<void> {
    console.log(`AppInitialize started`);

    const settings = await this.initAppSettings();
    await this.initAppContext(window, settings);
    await this.localeService.initLocale();

    this.gainsightService.initialize(window, settings.gainsight.tag);
    this.iconRegistryService.registerIcons();
    this.importService.subscribeImportWatcher();
    this.downloadService.subscribeDownloadWatcher();
    this.ingestionService.subscribeScandataWatcher();

    this.setUrlQuery();

    this.logger.debug(`AppInitialize complete`);
  }

  async initAppSettings(): Promise<AppSettings> {
    const response: Response = await fetch('appsettings.json');
    const settings: AppSettings = await response.json();
    await firstValueFrom(this.store.dispatch(new SetSettings(settings)));

    this.logger.debug('AppSettings loaded', { settings });
    return settings;
  }

  async initAppContext(window: Window, settings: AppSettings): Promise<void> {
    const workspace = await this.connectService.getWorkspace(window.parent);
    const context = isDefined(workspace) ? AppContext.ConnectExtension : settings.appContext;
    await firstValueFrom(this.store.dispatch(new SetContext(context)));
  }

  loadAppData() {
    this.logger.debug(`loadAppData started`);

    return this.store.dispatch(new SetIsLoading(true)).pipe(
      switchMap(() => this.projectRegionService.loadProjectRegion()),
      switchMap(() => this.userService.loadUser()),
      switchMap(() => this.featureFlagsConfigService.loadFeatureFlags()),
      switchMap(() => this.gainsightService.identifyUser()),
      switchMap(() => this.gainsightService.trackProject()),
      switchMap(() => this.gainsightService.detectGpu()),
      switchMap(() => this.scandataService.loadScandata()),
      switchMap(() => this.featureLayerService.loadFeatures()),
      tap(() => this.logger.debug(`loadAppData complete`)),
    );
  }

  private setUrlQuery() {
    const query = window.location.search || '';
    if (isDefined(query) && query.length > 0) this.store.dispatch(new SetUrlQuery(query));
  }
}
